import dynamic from "@redux-dynostore/react-redux";
import subspaced from "@redux-dynostore/react-redux-subspace";
import { attachReducer } from "@redux-dynostore/redux-subspace";
import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { connect } from "react-redux";
import { studyEntriesTableReducer } from "./StudyEntriesTable.store";
import {
  mapDispatchToProps,
  mapStateToProps,
} from "./StudyEntriesTable.store.js";
import StudyEntriesTable from "../../../library/LCF401/StudyEntriesTable/StudyEntriesTable";
import CustomModal from "../../../ui-library/CustomModal/CustomModal";
import DietDiary from "../../LCF401/DietDiary/DietDiary";
import {
  DietDairyTypes,
  dietDairyTypeToString,
} from "../../LCF401/DietDiary/DietDiary.constants";
import { ToastType } from "../../../ToastManager/constants";
import { sendNotification } from "../../../ToastManager/ToastManager";
import { validDateString } from "../../../../modules/utils";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

export const SmartStudyEntriesTable = (props) => {
  const {
    dateRange: { startDate = null, endDate = null } = {},
    fetchEntriesTableData,
    resetState,
    entries,
    totalItems,
    searchedParams,
    canCreateEntriesMealLog,
    canEditEntriesMealLog,
    canViewDetailMealLog,
    study,
    participantId,
    userData,
    onUpdate,
    loading,
    dietMenuItems,
    fetchDietDiaryMenuItems,
  } = props;

  const { t } = useTranslation();

  const [selectedDietDiaryOption, setSelectedDietDiaryOption] = useState(null);
  const [dietDiaryModalTitle, setDietDiaryModalTitle] = useState("");

  const dietDiaryOptions = useMemo(() => {
    const items =
      dietMenuItems?.map((item, i) => ({
        id: item.menuDisplayMealItemID,
        order: i,
        defaultItems: JSON.parse(item.defaultItemsJson),
        title: `${t("appText.add")} ${dietDairyTypeToString(
          item.dietDiaryType
        )}`,
        text: dietDairyTypeToString(item.dietDiaryType),
        type: item.dietDiaryType,
      })) || [];

    items.unshift({
      id: "1",
      order: -1,
      title: `${t("appText.add")} ${dietDairyTypeToString(
        DietDairyTypes.FOOD_DRINK
      )}`,
      text: dietDairyTypeToString(DietDairyTypes.FOOD_DRINK),
      type: DietDairyTypes.FOOD_DRINK,
    });

    return {
      ...items?.map((item) => ({
        ...item,
        isVisible: true,
        onClick: () => {
          setSelectedDietDiaryOption(item);
        },
      })),
    };
  }, [dietMenuItems, t]);

  useEffect(() => {
    fetchDietDiaryMenuItems();
  }, [fetchDietDiaryMenuItems]);

  const getData = useCallback(
    (params) => {
      fetchEntriesTableData(params, startDate, endDate, participantId).catch(
        (error) => {
          sendNotification(error, ToastType.ERROR);
        }
      );
    },
    [fetchEntriesTableData, startDate, endDate, participantId]
  );

  const onCloseDietDiaryModal = () => {
    setSelectedDietDiaryOption(null);
  };

  const onSubmitNewMeal = () => {
    setSelectedDietDiaryOption(null);
    onUpdate();
    getData(searchedParams); // re-fetch dates and data
  };

  const editMeal = (meal) => {
    const type = JSON.parse(meal.json)?.type ?? DietDairyTypes.FOOD_DRINK;

    setDietDiaryModalTitle(
      `${t("appText.editing")} ${userData?.participantUgnxId} ${t(
        "appText.entry"
      )}`
    );

    setSelectedDietDiaryOption({
      mealId: meal.mealId,
      jsonData: meal.json,
      date: meal.dateTime,
      type: type,
      reason: meal.reason,
      is24hRecall: meal.is24hRecall,
      hceMceEvent: meal.hceMceEvent,
    });
  };

  const viewMeal = (meal) => {
    const type = JSON.parse(meal.json)?.type ?? DietDairyTypes.FOOD_DRINK;

    setDietDiaryModalTitle(
      `${t("appText.view")} ${dietDairyTypeToString(type)}`
    );

    setSelectedDietDiaryOption({
      mealId: meal.mealId,
      jsonData: meal.json,
      date: meal.dateTime,
      readOnly: true,
      type: type,
      reason: meal.reason,
      reasonForChange: meal.reasonForChange,
      is24hRecall: meal.is24hRecall,
      hceMceEvent: meal.hceMceEvent,
    });
  };

  useEffect(() => {
    if (totalItems === null) {
      if (validDateString(startDate) && validDateString(endDate)) {
        getData(searchedParams);
      }
    }
  }, [endDate, getData, searchedParams, startDate, totalItems]);

  useEffect(() => {
    if (validDateString(startDate) && validDateString(endDate)) {
      getData(searchedParams);
    }
  }, [endDate, getData, searchedParams, startDate]);

  useEffect(() => {
    return () => resetState();
  }, [resetState]);

  const minDateMeal = useMemo(() => {
    return userData?.participantDateOfEnrollment
      ? moment(userData?.participantDateOfEnrollment).toDate()
      : null;
  }, [userData]);

  return (
    <>
      <CustomModal
        show={selectedDietDiaryOption !== null}
        title={dietDiaryModalTitle ?? ""}
        size="lg"
        onClose={() => onCloseDietDiaryModal()}
      >
        <DietDiary
          type={selectedDietDiaryOption?.type}
          participantId={participantId}
          onSubmit={() => onSubmitNewMeal()}
          onClose={() => onCloseDietDiaryModal()}
          maxDate={moment().toDate()}
          minDate={minDateMeal}
          readOnly={selectedDietDiaryOption?.readOnly}
          mealId={selectedDietDiaryOption?.mealId}
          mealDate={selectedDietDiaryOption?.date}
          mealJsonData={selectedDietDiaryOption?.jsonData}
          dietDiaryOption={selectedDietDiaryOption}
          setModalTitle={setDietDiaryModalTitle}
          allEntries={entries}
          existingReason={selectedDietDiaryOption?.reason}
          existingReasonForChange={selectedDietDiaryOption?.reasonForChange}
          studyId={study}
          defaultItems={selectedDietDiaryOption?.defaultItems}
          mealIs24hRecall={selectedDietDiaryOption?.is24hRecall || false}
          mealHceMceEvent={selectedDietDiaryOption?.hceMceEvent || false}
        />
      </CustomModal>

      <StudyEntriesTable
        {...{ getData }}
        {...{ searchedParams }}
        {...{ entries }}
        totalitems={totalItems}
        loading={loading}
        {...{ canViewDetailMealLog }}
        {...{ canCreateEntriesMealLog }}
        {...{ canEditEntriesMealLog }}
        {...{ editMeal }}
        {...{ viewMeal }}
        {...{ dietDiaryOptions }}
      />
    </>
  );
};

export default dynamic(
  "studyEntriesTableReducer",
  subspaced({
    mapExtraState: (state) => ({ loginReducer: state.loginReducer }),
  }),
  attachReducer(studyEntriesTableReducer)
)(connect(mapStateToProps, mapDispatchToProps)(SmartStudyEntriesTable));
